import { Point, Wall, CounterElement } from '../types';
import { distanceToLine } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS, PIXELS_PER_METER } from '../constants/floorPlanConstants';

const DEFAULT_COUNTER_WIDTH = 60; // Default depth from wall (perpendicular)
const DEFAULT_COUNTER_LENGTH = 150; // Default length along wall
const COUNTER_WALL_OFFSET = 0; // No offset - counter edge touches wall
const RESIZE_HANDLE_SIZE = 10; // Size of resize handles in pixels
const COUNTER_SNAP_THRESHOLD = 80; // Aggressive snapping - stays snapped within 80 pixels of wall
const MIN_SIDE_DISTANCE = 15; // Minimum distance from wall center to determine side - prevents on-wall snapping

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return start;
  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));
  return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

/**
 * Find the nearest wall to a point (for counter placement)
 * Uses aggressive snapping threshold to keep counters attached to walls
 * Only snaps if cursor is clearly on one side (not directly on the wall)
 */
export function findWallForCounter(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
  let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
  let minDistance = Infinity;

  for (const wall of walls) {
    const distance = distanceToLine(point, wall.start, wall.end);
    
    // Only snap if within threshold AND not too close to wall center (prevents on-wall placement)
    if (distance < COUNTER_SNAP_THRESHOLD && distance >= MIN_SIDE_DISTANCE) {
      if (distance < minDistance) {
        minDistance = distance;
        clickedWall = wall;
      }
    }
  }
  return clickedWall;
}

/**
 * Create a counter element with optional wall snapping
 * If no wall is found nearby, creates a free-floating counter
 */
export function createCounterWithAutoSnap(
  point: Point,
  walls: (Wall & { tempId: string, stableId: string })[],
  floor: number,
  length: number = DEFAULT_COUNTER_LENGTH,
  width: number = DEFAULT_COUNTER_WIDTH
): CounterElement {
  // Try to find a nearby wall to snap to
  const nearbyWall = findWallForCounter(point, walls);
  
  if (nearbyWall) {
    // Snap to wall
    return createCounterElement(point, nearbyWall, floor, length, width);
  } else {
    // Create free-floating counter at cursor position
    const rotation = 0; // Default horizontal orientation
    const halfLength = length / 2;
    
    return {
      id: `counter-${Date.now()}`,
      type: 'counter',
      wallId: '', // No wall association for free-floating counters
      start: { x: point.x - halfLength, y: point.y },
      end: { x: point.x + halfLength, y: point.y },
      width,
      length,
      floor,
      rotation,
      side: 1, // Default side
    };
  }
}

/**
 * Create a counter element snapped to a specific wall
 */
export function createCounterElement(
  point: Point,
  wall: Wall & { tempId: string, stableId: string },
  floor: number,
  length: number = DEFAULT_COUNTER_LENGTH,
  width: number = DEFAULT_COUNTER_WIDTH
): CounterElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  
  // Snap point to wall
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate position along wall (normalized 0-1)
  let positionOnWall = distFromStart / wallLength;
  
  // Keep counter within wall bounds
  const halfCounterLengthOnWall = (length / 2) / wallLength;
  positionOnWall = Math.max(halfCounterLengthOnWall, Math.min(positionOnWall, 1 - halfCounterLengthOnWall));
  
  // Calculate counter center on wall
  const counterCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate perpendicular offset from wall
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Determine which side of the wall the click is on
  // Use the perpendicular distance from the wall to determine side more reliably
  const toPoint = { x: point.x - closestPointOnWall.x, y: point.y - closestPointOnWall.y };
  const distanceFromWall = toPoint.x * perpVector.x + toPoint.y * perpVector.y;
  
  // Only snap if there's a clear side preference (not directly on the wall)
  // Use sign of perpendicular distance to determine which side
  const side = distanceFromWall >= 0 ? 1 : -1;
  
  // Calculate start and end points along the wall
  const halfLength = length / 2;
  const startOnWall = {
    x: counterCenterOnWall.x - (wallDx / wallLength) * halfLength,
    y: counterCenterOnWall.y - (wallDy / wallLength) * halfLength,
  };
  const endOnWall = {
    x: counterCenterOnWall.x + (wallDx / wallLength) * halfLength,
    y: counterCenterOnWall.y + (wallDy / wallLength) * halfLength,
  };
  
  // Offset from wall to interior side - position the counter edge exactly at wall surface
  // Counter extends perpendicular to the wall by 'width' amount
  const totalOffset = (WALL_THICKNESS / 2) * side;
  
  const start = {
    x: startOnWall.x + perpVector.x * totalOffset,
    y: startOnWall.y + perpVector.y * totalOffset,
  };
  
  const end = {
    x: endOnWall.x + perpVector.x * totalOffset,
    y: endOnWall.y + perpVector.y * totalOffset,
  };
  
  return {
    id: `counter-${Date.now()}`,
    type: 'counter',
    wallId: wall.stableId,
    start,
    end,
    width,
    length,
    floor,
    rotation: wallAngle,
    side,
  };
}

/**
 * Draw counter elements
 */
export function drawCounters(
  ctx: CanvasRenderingContext2D,
  elements: (CounterElement)[],
  currentFloor: number,
  scale: number,
  viewOffset: Point,
  isSelected?: (id: string) => boolean,
  showResizeHandles?: boolean,
  isEstate: boolean = false
) {
  const counters = elements.filter(el => el.floor === currentFloor);
  
  counters.forEach(counter => {
    ctx.save();
    
    // Draw rectangle from start to end with width
    const dx = counter.end.x - counter.start.x;
    const dy = counter.end.y - counter.start.y;
    const perpDx = -dy;
    const perpDy = dx;
    const perpLen = Math.hypot(perpDx, perpDy);
    const perpNormX = perpDx / perpLen;
    const perpNormY = perpDy / perpLen;
    
    // Calculate four corners of the counter
    const corners = [
      { x: counter.start.x, y: counter.start.y },
      { x: counter.end.x, y: counter.end.y },
      { x: counter.end.x + perpNormX * counter.width, y: counter.end.y + perpNormY * counter.width },
      { x: counter.start.x + perpNormX * counter.width, y: counter.start.y + perpNormY * counter.width },
    ];
    
    const selected = isSelected && isSelected(counter.id);
    
    // Fill counter
    // 🏢 ESTATE MODE: Use white for professional B&W look
    ctx.fillStyle = isEstate ? '#ffffff' : (selected ? '#93c5fd' : '#e5e7eb');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    corners.forEach((corner, i) => {
      if (i > 0) ctx.lineTo(corner.x, corner.y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw resize handles if counter tool is active or counter is selected
    if (selected || showResizeHandles) {
      const handleSize = 10 / scale; // Bigger handles for easier interaction
      ctx.fillStyle = '#3b82f6';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / scale;
      
      // Corner handles for length adjustment along wall
      [corners[0], corners[1]].forEach(corner => {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });
      
      // Corner handles for width/length adjustment (outer corners)
      [corners[2], corners[3]].forEach(corner => {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });
      
      // Move/drag control at center (for all counters - wall-snapped and free-floating)
      const bottomMidX = (corners[0].x + corners[1].x) / 2;
      const bottomMidY = (corners[0].y + corners[1].y) / 2;
      
      ctx.fillStyle = '#f59e0b'; // Orange for move
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath();
      ctx.arc(bottomMidX, bottomMidY, handleSize * 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      if (!counter.wallId) {
        // Free-floating: Draw 4-way move icon
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5 / scale;
        const arrowSize = handleSize * 0.5;
        // Up arrow
        ctx.beginPath();
        ctx.moveTo(bottomMidX, bottomMidY - arrowSize);
        ctx.lineTo(bottomMidX, bottomMidY - arrowSize * 0.2);
        ctx.stroke();
        // Down arrow
        ctx.beginPath();
        ctx.moveTo(bottomMidX, bottomMidY + arrowSize);
        ctx.lineTo(bottomMidX, bottomMidY + arrowSize * 0.2);
        ctx.stroke();
        // Left arrow
        ctx.beginPath();
        ctx.moveTo(bottomMidX - arrowSize, bottomMidY);
        ctx.lineTo(bottomMidX - arrowSize * 0.2, bottomMidY);
        ctx.stroke();
        // Right arrow
        ctx.beginPath();
        ctx.moveTo(bottomMidX + arrowSize, bottomMidY);
        ctx.lineTo(bottomMidX + arrowSize * 0.2, bottomMidY);
        ctx.stroke();
      } else {
        // Wall-snapped: Draw 2-way move icon (along wall)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5 / scale;
        const arrowSize = handleSize * 0.5;
        const wallAngle = counter.rotation;
        
        // Left arrow along wall
        const leftX1 = bottomMidX - Math.cos(wallAngle) * arrowSize;
        const leftY1 = bottomMidY - Math.sin(wallAngle) * arrowSize;
        const leftX2 = bottomMidX - Math.cos(wallAngle) * arrowSize * 0.2;
        const leftY2 = bottomMidY - Math.sin(wallAngle) * arrowSize * 0.2;
        ctx.beginPath();
        ctx.moveTo(leftX1, leftY1);
        ctx.lineTo(leftX2, leftY2);
        ctx.stroke();
        
        // Right arrow along wall
        const rightX1 = bottomMidX + Math.cos(wallAngle) * arrowSize;
        const rightY1 = bottomMidY + Math.sin(wallAngle) * arrowSize;
        const rightX2 = bottomMidX + Math.cos(wallAngle) * arrowSize * 0.2;
        const rightY2 = bottomMidY + Math.sin(wallAngle) * arrowSize * 0.2;
        ctx.beginPath();
        ctx.moveTo(rightX1, rightY1);
        ctx.lineTo(rightX2, rightY2);
        ctx.stroke();
      }
      
      // For free-floating counters, add rotation control
      if (!counter.wallId) {
        // Rotation control at top (outer edge center)
        const topMidX = (corners[2].x + corners[3].x) / 2;
        const topMidY = (corners[2].y + corners[3].y) / 2;
        
        ctx.fillStyle = '#10b981'; // Green for rotation
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 / scale;
        ctx.beginPath();
        ctx.arc(topMidX, topMidY, handleSize * 1.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        // Draw rotation icon (curved arrow)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5 / scale;
        ctx.beginPath();
        ctx.arc(topMidX, topMidY, handleSize * 0.6, 0, Math.PI * 1.5);
        ctx.stroke();
        // Arrow head
        ctx.beginPath();
        ctx.moveTo(topMidX - handleSize * 0.4, topMidY - handleSize * 0.6);
        ctx.lineTo(topMidX - handleSize * 0.6, topMidY - handleSize * 0.3);
        ctx.lineTo(topMidX - handleSize * 0.2, topMidY - handleSize * 0.3);
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
    }
    
    // Draw measurements
    const lengthMeters = (counter.length / PIXELS_PER_METER).toFixed(2);
    const widthMeters = (counter.width / PIXELS_PER_METER).toFixed(2);
    
    // Draw length measurement along counter - centered inside the counter
    const midX = (counter.start.x + counter.end.x) / 2;
    const midY = (counter.start.y + counter.end.y) / 2;
    
    // Offset the text inside the counter (perpendicular to length)
    const offsetDistance = counter.width / 4; // Position at 1/4 of the width inside
    const offsetX = midX + perpNormX * offsetDistance;
    const offsetY = midY + perpNormY * offsetDistance;
    
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.rotate(counter.rotation);
    ctx.fillText(`${lengthMeters}m`, 0, 0);
    ctx.restore();
    
    // Draw width measurement perpendicular to counter - centered on opposite side edge with padding
    // Position at the center of the opposite side edge (between corners[1] and corners[2])
    const widthMidX = (corners[1].x + corners[2].x) / 2;
    const widthMidY = (corners[1].y + corners[2].y) / 2;
    
    // Calculate rotation for width text (perpendicular to counter)
    let widthRotation = counter.rotation + Math.PI / 2;
    
    // Normalize rotation to 0-2π range
    while (widthRotation < 0) widthRotation += Math.PI * 2;
    while (widthRotation >= Math.PI * 2) widthRotation -= Math.PI * 2;
    
    // Flip text if it would be upside down (between π/2 and 3π/2)
    let textOffset = 20;
    if (widthRotation > Math.PI / 2 && widthRotation < Math.PI * 3 / 2) {
      widthRotation += Math.PI; // Flip 180 degrees
      textOffset = -20; // Flip offset direction too
    }
    
    ctx.save();
    ctx.translate(widthMidX, widthMidY);
    ctx.rotate(widthRotation);
    ctx.fillText(`${widthMeters}m`, 0, textOffset);
    ctx.restore();
    
    ctx.restore();
  });
}

/**
 * Draw counter preview during placement
 */
export function drawCounterPreview(
  ctx: CanvasRenderingContext2D,
  preview: CounterElement | null,
  scale: number
) {
  if (!preview) return;
  
  ctx.save();
  ctx.globalAlpha = 0.6;
  
  const dx = preview.end.x - preview.start.x;
  const dy = preview.end.y - preview.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  const corners = [
    { x: preview.start.x, y: preview.start.y },
    { x: preview.end.x, y: preview.end.y },
    { x: preview.end.x + perpNormX * preview.width, y: preview.end.y + perpNormY * preview.width },
    { x: preview.start.x + perpNormX * preview.width, y: preview.start.y + perpNormY * preview.width },
  ];
  
  ctx.fillStyle = '#93c5fd';
  ctx.strokeStyle = '#3b82f6';
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 5]);
  
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  corners.forEach((corner, i) => {
    if (i > 0) ctx.lineTo(corner.x, corner.y);
  });
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  ctx.restore();
}

/**
 * Check if a point is inside a counter (for selection/resizing)
 */
export function isPointInsideCounter(point: Point, counter: CounterElement): boolean {
  const dx = counter.end.x - counter.start.x;
  const dy = counter.end.y - counter.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  const corners = [
    { x: counter.start.x, y: counter.start.y },
    { x: counter.end.x, y: counter.end.y },
    { x: counter.end.x + perpNormX * counter.width, y: counter.end.y + perpNormY * counter.width },
    { x: counter.start.x + perpNormX * counter.width, y: counter.start.y + perpNormY * counter.width },
  ];
  
  // Point-in-polygon test
  let inside = false;
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const xi = corners[i].x, yi = corners[i].y;
    const xj = corners[j].x, yj = corners[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
}

/**
 * Check if point is on rotation control for free-floating counter
 */
export function isPointOnRotationControl(point: Point, counter: CounterElement, tolerance: number = 12): boolean {
  if (counter.wallId) return false; // Only for free-floating counters
  
  const dx = counter.end.x - counter.start.x;
  const dy = counter.end.y - counter.start.y;
  const perpX = -dy;
  const perpY = dx;
  const perpLen = Math.hypot(perpX, perpY);
  const perpNormX = perpX / perpLen;
  const perpNormY = perpY / perpLen;
  
  // Rotation control is at top (outer edge center)
  const topMidX = (counter.end.x + perpNormX * counter.width + counter.start.x + perpNormX * counter.width) / 2;
  const topMidY = (counter.end.y + perpNormY * counter.width + counter.start.y + perpNormY * counter.width) / 2;
  
  const dist = Math.hypot(point.x - topMidX, point.y - topMidY);
  return dist < tolerance;
}

/**
 * Check if point is on move/drag control for counter (both wall-snapped and free-floating)
 */
export function isPointOnMoveControl(point: Point, counter: CounterElement, tolerance: number = 12): boolean {
  // Move control is at bottom (wall edge center) for all counters
  const bottomMidX = (counter.start.x + counter.end.x) / 2;
  const bottomMidY = (counter.start.y + counter.end.y) / 2;
  
  const dist = Math.hypot(point.x - bottomMidX, point.y - bottomMidY);
  return dist < tolerance;
}

/**
 * Get resize handle at a point for a counter
 * Returns: 'length-start', 'length-end', 'width', or null
 */
export function getCounterResizeHandle(point: Point, counter: CounterElement, tolerance: number = RESIZE_HANDLE_SIZE): string | null {
  const dx = counter.end.x - counter.start.x;
  const dy = counter.end.y - counter.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  // Calculate four corners
  const corners = [
    { x: counter.start.x, y: counter.start.y, handle: 'length-start' },
    { x: counter.end.x, y: counter.end.y, handle: 'length-end' },
    { x: counter.end.x + perpNormX * counter.width, y: counter.end.y + perpNormY * counter.width, handle: 'width-end' },
    { x: counter.start.x + perpNormX * counter.width, y: counter.start.y + perpNormY * counter.width, handle: 'width-start' },
  ];
  
  // Check corner handles first (for length adjustment)
  for (const corner of corners) {
    const dist = Math.hypot(point.x - corner.x, point.y - corner.y);
    if (dist < tolerance) {
      return corner.handle;
    }
  }
  
  // Check edge handles for width adjustment (perpendicular edge)
  const widthEdgeMid = {
    x: (corners[2].x + corners[3].x) / 2,
    y: (corners[2].y + corners[3].y) / 2,
  };
  const distToWidthEdge = Math.hypot(point.x - widthEdgeMid.x, point.y - widthEdgeMid.y);
  if (distToWidthEdge < tolerance) {
    return 'width';
  }
  
  return null;
}

/**
 * Resize a counter element while maintaining wall snapping
 */
export function resizeCounter(
  counter: CounterElement,
  handle: string,
  point: Point,
  wall: Wall & { tempId: string, stableId: string }
): CounterElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  let newLength = counter.length;
  let newWidth = counter.width;
  let newStart = counter.start;
  let newEnd = counter.end;
  
  if (handle === 'length-start' || handle === 'length-end') {
    // Adjust length along the wall - project point onto wall
    const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromWallStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
    const positionOnWall = distFromWallStart / wallLength;
    
    // Wall edge offset (counter sits on wall surface)
    const wallEdgeOffset = (WALL_THICKNESS / 2) * counter.side;
    
    if (handle === 'length-start') {
      // Resize from start handle - move start, keep end fixed
      // Find where end currently is on the wall
      const currentEndOnWall = {
        x: counter.end.x - perpVector.x * wallEdgeOffset,
        y: counter.end.y - perpVector.y * wallEdgeOffset,
      };
      const endDistFromStart = Math.hypot(currentEndOnWall.x - wall.start.x, currentEndOnWall.y - wall.start.y);
      const endPos = endDistFromStart / wallLength;
      
      // Constrain new start position
      const newStartPos = Math.max(0, Math.min(positionOnWall, endPos - 0.05));
      newLength = Math.abs(endPos - newStartPos) * wallLength;
      
      // Calculate new start position on wall
      const newStartOnWall = {
        x: wall.start.x + (wallDx / wallLength) * newStartPos * wallLength,
        y: wall.start.y + (wallDy / wallLength) * newStartPos * wallLength,
      };
      
      // Apply wall edge offset to get counter position
      newStart = {
        x: newStartOnWall.x + perpVector.x * wallEdgeOffset,
        y: newStartOnWall.y + perpVector.y * wallEdgeOffset,
      };
    } else {
      // Resize from end handle - move end, keep start fixed
      // Find where start currently is on the wall
      const currentStartOnWall = {
        x: counter.start.x - perpVector.x * wallEdgeOffset,
        y: counter.start.y - perpVector.y * wallEdgeOffset,
      };
      const startDistFromStart = Math.hypot(currentStartOnWall.x - wall.start.x, currentStartOnWall.y - wall.start.y);
      const startPos = startDistFromStart / wallLength;
      
      // Constrain new end position
      const newEndPos = Math.max(startPos + 0.05, Math.min(1, positionOnWall));
      newLength = Math.abs(newEndPos - startPos) * wallLength;
      
      // Calculate new end position on wall
      const newEndOnWall = {
        x: wall.start.x + (wallDx / wallLength) * newEndPos * wallLength,
        y: wall.start.y + (wallDy / wallLength) * newEndPos * wallLength,
      };
      
      // Apply wall edge offset to get counter position
      newEnd = {
        x: newEndOnWall.x + perpVector.x * wallEdgeOffset,
        y: newEndOnWall.y + perpVector.y * wallEdgeOffset,
      };
    }
  } else if (handle === 'width' || handle === 'width-start' || handle === 'width-end') {
    // Adjust width perpendicular to the wall
    // counter.start and counter.end are already positioned on the wall edge
    // Width extends perpendicular from these points
    
    // Determine which edge point to measure from based on handle
    const referencePoint = handle === 'width-start' ? counter.start : 
                           handle === 'width-end' ? counter.end :
                           { x: (counter.start.x + counter.end.x) / 2, y: (counter.start.y + counter.end.y) / 2 };
    
    // Calculate perpendicular distance from reference point to cursor
    const toPoint = { x: point.x - referencePoint.x, y: point.y - referencePoint.y };
    const perpDist = toPoint.x * perpVector.x + toPoint.y * perpVector.y;
    
    // Width is the perpendicular distance from wall edge to cursor
    // (start/end are on wall edge, width extends perpendicular from there)
    newWidth = Math.max(20, Math.abs(perpDist));
    
    // Keep start and end at their current positions (they're on the wall edge)
    // Only the width value changes
    newStart = counter.start;
    newEnd = counter.end;
  }
  
  return {
    ...counter,
    start: newStart,
    end: newEnd,
    width: newWidth,
    length: newLength,
  };
}

/**
 * Drag a wall-snapped counter along the wall
 */
export function dragCounterAlongWall(
  counter: CounterElement,
  point: Point,
  wall: Wall & { tempId: string, stableId: string }
): CounterElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Project cursor point onto the wall
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromWallStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate new position along wall (normalized 0-1)
  let positionOnWall = distFromWallStart / wallLength;
  
  // Keep counter within wall bounds
  const halfCounterLengthOnWall = (counter.length / 2) / wallLength;
  positionOnWall = Math.max(halfCounterLengthOnWall, Math.min(positionOnWall, 1 - halfCounterLengthOnWall));
  
  // Calculate new counter center on wall
  const counterCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate new start and end points along the wall
  const halfLength = counter.length / 2;
  const startOnWall = {
    x: counterCenterOnWall.x - (wallDx / wallLength) * halfLength,
    y: counterCenterOnWall.y - (wallDy / wallLength) * halfLength,
  };
  const endOnWall = {
    x: counterCenterOnWall.x + (wallDx / wallLength) * halfLength,
    y: counterCenterOnWall.y + (wallDy / wallLength) * halfLength,
  };
  
  // Apply wall edge offset
  const wallEdgeOffset = (WALL_THICKNESS / 2) * counter.side;
  
  const newStart = {
    x: startOnWall.x + perpVector.x * wallEdgeOffset,
    y: startOnWall.y + perpVector.y * wallEdgeOffset,
  };
  
  const newEnd = {
    x: endOnWall.x + perpVector.x * wallEdgeOffset,
    y: endOnWall.y + perpVector.y * wallEdgeOffset,
  };
  
  return {
    ...counter,
    start: newStart,
    end: newEnd,
  };
}
